/** @prettier */

import {useEffect, useState} from 'react';
import {utcToZonedTime} from 'date-fns-tz';
import {differenceInCalendarDays, format, sub} from 'date-fns';

//API import
import {getStatesSitesModbus} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {GraphFormType, GraphDataRange} from '../../Type/TypeDef';

import {graph} from '../../define';

interface BasicUseChangeStartEndDateArgIF {
  siteID: string | undefined; //サイトID
  graphStartDate: string; //グラフ開始日
  graphEndDate: string; //グラフ最終日
  graphType: GraphFormType; //グラフの種類
  timeZone: string; //タイムゾーン
  dateRange: GraphDataRange; //グラフの範囲
}
interface UseModbusOUChangeStartEndDateArgIF
  extends BasicUseChangeStartEndDateArgIF {
  selectedItem: (keyof apiIF.resParameters.modbusOUStates)[]; //表示項目
}
interface UseModbusIUChangeStartEndDateArgIF
  extends BasicUseChangeStartEndDateArgIF {
  selectedItem: (keyof apiIF.resParameters.modbusIUStates)[]; //表示項目
}
const useModbusOUChangeStartEndDate = ({
  siteID,
  graphStartDate,
  graphEndDate,
  graphType,
  timeZone,
  dateRange,
  selectedItem,
}: UseModbusOUChangeStartEndDateArgIF) => {
  const [apiWait, setAPIWait] = useState(false); //API待機中判定
  const [trendDisable, setTrendDisable] = useState(false); //トレンドグラフ選択可否
  const [powerDisable, setPowerDisable] = useState(false); //電力グラフ選択可否
  const [graphDispError, setGraphDispError] = useState(false); //
  const [csvFileNameDate, setCSVFileNameDate] = useState(''); //CSVファイルの名前（日付部分）
  const [modbusStateData, setModbusStateData] = useState<
    {date: string; states: apiIF.resParameters.modbusOUStates[]}[] | null
  >(null); //Modbus室外機状態データ

  //グラフボタン押下、他ユニットのグラフを表示、カレンダ日付変更に日時を更新し、グラフ描画用データを取得する
  const fieldsParam = selectedItem.join(',');
  useEffect(() => {
    if (siteID === undefined || graphStartDate === '' || graphEndDate === '') {
      return;
    }
    setAPIWait(true);
    setTrendDisable(true);
    setPowerDisable(true);

    //APIに渡すクエリの決定
    const {fields, everyMinute, maxResult} = createAPIQuery(
      graphType,
      fieldsParam,
      dateRange
    );
    //csvファイルに出力する日付情報を作成
    const {csvFileTitle} = createTitle(
      graphStartDate,
      graphEndDate,
      timeZone,
      dateRange
    );

    //グラフボタンと日付変更が同時に発生した場合、useEffectが2回走り、APIの完了タイミングによって
    //day選択時にweekの結果を表示しようとするので、これを防ぐため下記条件を実装
    const distance = differenceInCalendarDays(
      new Date(graphEndDate),
      new Date(graphStartDate)
    );
    if (
      (dateRange === 'day' && distance < 2) ||
      (dateRange === 'week' && distance >= 2 && distance <= 7) ||
      (dateRange === 'month' && distance > 7 && distance <= 31) ||
      (dateRange === 'year' && distance > 31 && distance <= 366) ||
      (dateRange === 'tenYear' && distance > 366 && distance <= 3650)
    ) {
      setCSVFileNameDate(csvFileTitle);

      setGraphDispError(false);
      getStatesSitesModbus(siteID, {
        'fields': fields,
        'after-time': graphStartDate,
        'before-time': graphEndDate,
        'every-minute': everyMinute,
        'max-result': maxResult,
      })
        .then(response => {
          setModbusStateData(
            response['modbus-states'].map(data => ({
              date: data.date,
              states: data['outdoor-unit-states'],
            }))
          );
        })
        .catch(() => {
          setGraphDispError(true);
          setTrendDisable(false);
          setPowerDisable(false);
        })
        .finally(() => {
          setAPIWait(false);
        });
    }
  }, [graphType, graphStartDate, graphEndDate, fieldsParam]);

  return {
    modbusStateData,
    csvFileNameDate,
    trendDisable,
    powerDisable,
    graphDispError,
    apiWait,
  };
};
const useModbusIUChangeStartEndDate = ({
  siteID,
  graphStartDate,
  graphEndDate,
  graphType,
  timeZone,
  dateRange,
  selectedItem,
}: UseModbusIUChangeStartEndDateArgIF) => {
  const [apiWait, setAPIWait] = useState(false); //API待機中判定
  const [graphDispError, setGraphDispError] = useState(false); //
  const [graphTitle, setGraphTitle] = useState(''); //グラフのタイトル（日付範囲）Weekグラフのみ使用
  const [csvFileNameDate, setCSVFileNameDate] = useState(''); //CSVファイルの名前（日付部分）
  const [modbusStateData, setModbusStateData] = useState<
    {date: string; states: apiIF.resParameters.modbusIUStates[]}[] | null
  >(null); //Modbus室内機状態データ

  //グラフボタン押下、他ユニットのグラフを表示、カレンダ日付変更に日時を更新し、グラフ描画用データを取得する
  const fieldsParam = selectedItem.join(',');
  useEffect(() => {
    if (siteID === undefined || graphStartDate === '' || graphEndDate === '') {
      return;
    }
    setAPIWait(true);

    //APIに渡すクエリの決定
    const {fields, everyMinute, maxResult} = createAPIQuery(
      graphType,
      fieldsParam,
      dateRange
    );
    //csvファイルに出力する日付情報を作成
    const {graphTitle, csvFileTitle} = createTitle(
      graphStartDate,
      graphEndDate,
      timeZone,
      dateRange
    );

    //グラフボタンと日付変更が同時に発生した場合、useEffectが2回走り、APIの完了タイミングによって
    //day選択時にweekの結果を表示しようとするので、これを防ぐため下記条件を実装
    const distance = differenceInCalendarDays(
      new Date(graphEndDate),
      new Date(graphStartDate)
    );
    if (
      (dateRange === 'day' && distance < 2) ||
      (dateRange === 'week' && distance >= 2 && distance <= 7) ||
      (dateRange === 'month' && distance > 7 && distance <= 31) ||
      (dateRange === 'year' && distance > 31 && distance <= 366) ||
      (dateRange === 'tenYear' && distance > 366 && distance <= 3653)
    ) {
      setGraphTitle(graphTitle);
      setCSVFileNameDate(csvFileTitle);
      setGraphDispError(false);
      getStatesSitesModbus(siteID, {
        'fields': fields,
        'after-time': graphStartDate,
        'before-time': graphEndDate,
        'every-minute': everyMinute,
        'max-result': maxResult,
      })
        .then(response => {
          setModbusStateData(
            response['modbus-states'].map(data => ({
              date: data.date,
              states: data['indoor-unit-states'],
            }))
          );
        })
        .catch(() => {
          setGraphDispError(true);
        })
        .finally(() => {
          setAPIWait(false);
        });
    }
  }, [graphType, graphStartDate, graphEndDate, fieldsParam]);

  return {
    modbusStateData,
    graphTitle,
    csvFileNameDate,
    graphDispError,
    apiWait,
  };
};

/**
 * APIに渡すクエリを作成
 * @param graphType グラフの種類
 * @param fieldsParam 取得したい状態値
 * @param dateRange グラフの範囲
 * @returns クエリ
 */
const createAPIQuery = (
  graphType: GraphFormType,
  fieldsParam: string,
  dateRange: GraphDataRange
) => {
  let fields = '';
  if (graphType === 'line') fields = fieldsParam;
  if (graphType === 'bar') fields = 'power-consumption';

  let everyMinute;
  let maxResult;

  switch (dateRange) {
    case 'day':
    default:
      everyMinute =
        graphType === 'line'
          ? graph.DAY_LINE_DATE_RANGE
          : graph.DAY_BAR_DATE_RANGE;
      maxResult =
        graphType === 'line'
          ? graph.DAY_LINE_API_MAX_RESULT
          : graph.DAY_BAR_API_MAX_RESULT;
      break;
    case 'week':
      everyMinute =
        graphType === 'line'
          ? graph.WEEK_LINE_DATE_RANGE
          : graph.WEEK_BAR_DATE_RANGE;
      maxResult =
        graphType === 'line'
          ? graph.WEEK_LINE_API_MAX_RESULT
          : graph.WEEK_BAR_API_MAX_RESULT;
      break;
    case 'month':
      everyMinute = graph.MONTH_BAR_DATE_RANGE;
      maxResult = graph.MONTH_BAR_API_MAX_RESULT;
      break;
    case 'year':
      everyMinute = graph.YEAR_BAR_DATE_RANGE;
      maxResult = graph.YEAR_BAR_API_MAX_RESULT;
      break;
    case 'tenYear':
      everyMinute = graph.TEN_YEAR_BAR_DATE_RANGE;
      maxResult = graph.TEN_YEAR_BAR_API_MAX_RESULT;
      break;
  }

  return {fields: fields, everyMinute: everyMinute, maxResult: maxResult};
};

/**
 * グラフ・CSVファイルのタイトルを作成
 * @param graphStartDate グラフ開始日
 * @param graphEndDate グラフ終了日
 * @param timeZone タイムゾーン
 * @param dateRange グラフの範囲
 * @returns タイトル
 */
const createTitle = (
  graphStartDate: string,
  graphEndDate: string,
  timeZone: string,
  dateRange: GraphDataRange
) => {
  let graphTitle; // グラフ画面に表示する日時
  //グラフタイトルに表示する日付情報(開始日)を作成
  const start = format(
    utcToZonedTime(graphStartDate, timeZone),
    'dd.MMMM yyyy'
  );
  let csvFileTitle; // csvに表示する日時

  switch (dateRange) {
    case 'day':
    default:
      graphTitle = start;
      csvFileTitle = format(
        utcToZonedTime(graphStartDate, timeZone),
        'yyyyMMdd'
      );
      break;
    case 'week':
      //グラフタイトルに表示する日付情報(終了日)を作成
      const end = format(
        utcToZonedTime(sub(new Date(graphEndDate), {days: 1}), timeZone),
        'dd.MMMM yyyy'
      );
      graphTitle = `${start}-${end}`;
      //1週間表示のcsvファイルに出力する日付情報を作成
      const tempCSVTitleStart = format(
        utcToZonedTime(graphStartDate, timeZone),
        'yyyyMMdd'
      );
      const tempCSVTitleEnd = format(
        utcToZonedTime(sub(new Date(graphEndDate), {days: 1}), timeZone),
        'yyyyMMdd'
      );
      csvFileTitle = `${tempCSVTitleStart}_${tempCSVTitleEnd}`;
      break;
    case 'month':
      //1カ月表示のcsvファイルに出力する日付情報を作成
      const monthStart = format(
        utcToZonedTime(graphStartDate, timeZone),
        'MMMM yyyy'
      );
      graphTitle = `${monthStart}`;
      const tempMonthCSVTitleStart = format(
        utcToZonedTime(graphStartDate, timeZone),
        'yyyyMM'
      );
      csvFileTitle = `${tempMonthCSVTitleStart}`;
      break;
    case 'year':
      //1年表示のcsvファイルに出力する日付情報を作成
      const yearStart = format(
        utcToZonedTime(graphStartDate, timeZone),
        'yyyy'
      );
      graphTitle = `${yearStart}`;
      csvFileTitle = `${yearStart}`;
      break;
    case 'tenYear':
      //10年表示のcsvファイルに出力するグラフの開始年を作成
      const tenYearStart = format(
        utcToZonedTime(graphStartDate, timeZone),
        'yyyy'
      );
      //10年表示のcsvファイルに出力するグラフの終了年を作成
      const tenYearEnd = format(
        utcToZonedTime(sub(new Date(graphEndDate), {days: 1}), timeZone),
        'yyyy'
      );
      graphTitle = `${tenYearStart}-${tenYearEnd}`;
      csvFileTitle = `${tenYearStart}_${tenYearEnd}`;
      break;
  }

  return {graphTitle: graphTitle, csvFileTitle: csvFileTitle};
};

export {useModbusOUChangeStartEndDate, useModbusIUChangeStartEndDate};
